import { useCallback } from 'react';
import { CarIcon } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';
import { toast } from '@/components';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { EngineService } from '@/services/engine/engine-service';
import { revalidateEntityViews } from '@/utils/swr';
import type { Vehicle, VehicleMergeResult } from '@/types/vehicle';
import { MergeIdentityModal } from '../MergeIdentityModal';
import { TargetIdentityPicker } from '../TargetIdentityPicker';
import type { MergeCandidate } from '../types';
import { getErrorDetail } from '../utils';
import { MergeCandidateRow } from '../MergeCandidateRow';
import { SelectedVehicle } from './SelectedVehicle';

interface MergeVehicleModalProps {
    isOpen: boolean;
    onClose: () => void;
    sourceVehicle: Vehicle;
    onSuccess: (result: VehicleMergeResult) => void;
    onSourceStale?: () => void;
}

export function MergeVehicleModal({
    isOpen,
    onClose,
    sourceVehicle,
    onSuccess,
    onSourceStale,
}: MergeVehicleModalProps) {
    const { t } = useTranslation(I18nNamespace.DIRECTORY);

    const sourceVehicleId = sourceVehicle.id;

    const fetchSuggestions = useCallback(
        async (sourceId: string): Promise<MergeCandidate[]> => {
            const response =
                await EngineService.getVehicleMergeSuggestions(sourceId);
            return response.suggestions.map((suggestion) => ({
                id: suggestion.vehicleId,
                name: suggestion.name ?? suggestion.licensePlate,
                imageUrl: suggestion.imageUrl,
                score: suggestion.similarityScore,
                subLabel: suggestion.name ? suggestion.licensePlate : null,
            }));
        },
        [],
    );

    const fetchTargets = useCallback(
        async ({
            search,
            sourceId,
            page,
            size,
        }: {
            search: string;
            sourceId: string;
            page: number;
            size: number;
        }) => {
            const response = await EngineService.searchVehicleMergeTargets({
                search,
                excludeVehicleIds: [sourceId],
                page,
                size,
            });
            return {
                items: response.items
                    .filter((target) => target.vehicleId !== sourceId)
                    .map((target) => ({
                        id: target.vehicleId,
                        name: target.name ?? target.licensePlate,
                        imageUrl: target.imageUrl,
                        score: null,
                        subLabel: target.name ? target.licensePlate : null,
                    })),
                total: response.page.totalItems,
            };
        },
        [],
    );

    const handleConfirm = useCallback(
        async (targetVehicleId: string) => {
            try {
                const result = await EngineService.mergeVehicles({
                    sourceVehicleId,
                    targetVehicleId,
                });
                toast.success(t('mergeVehicleModal.successMerge'));
                // The source vehicle is deleted by the merge; skip its cache
                // keys so we don't refetch a now-404 vehicle and surface a
                // spurious "failed to load" error.
                await revalidateEntityViews('vehicle', (key) =>
                    key.includes(sourceVehicleId),
                );
                onSuccess(result);
            } catch (error: unknown) {
                if (getErrorDetail(error) === 'SOURCE_NOT_FOUND') {
                    toast.info(t('mergeVehicleModal.errorAlreadyMerged'));
                    onSourceStale?.();
                    return;
                }
                toast.error(t('mergeVehicleModal.errorMerge'));
                throw error;
            }
        },
        [sourceVehicleId, onSuccess, onSourceStale, t],
    );

    return (
        <MergeIdentityModal
            isOpen={isOpen}
            onClose={onClose}
            titleText={t('mergeVehicleModal.title')}
            infoText={t('mergeVehicleModal.info')}
            submitText={t('mergeVehicleModal.merge')}
            isLink={false}
            onConfirm={handleConfirm}
            renderSource={() => (
                <SelectedVehicle
                    vehicle={sourceVehicle}
                    sectionLabel={t('mergeVehicleModal.selectedVehicle')}
                />
            )}
            renderTargetPicker={({ selectedId, onSelect, onClear }) => (
                <TargetIdentityPicker
                    sourceId={sourceVehicleId}
                    selectedId={selectedId}
                    onSelect={(id) => onSelect(id)}
                    onClear={onClear}
                    showSuggestions
                    sectionLabel={t('mergeVehicleModal.intoVehicle')}
                    swrKey="vehicle-merge-target-picker"
                    fallbackIcon={
                        <CarIcon className="text-grey-400" size={28} />
                    }
                    labels={{
                        searchPlaceholder: t(
                            'mergeVehicleModal.searchPlaceholder',
                        ),
                        empty: t('mergeVehicleModal.noVehiclesFound'),
                        select: t('mergeVehicleModal.selectVehicle'),
                        unknown: t('mergeVehicleModal.unnamedVehicle'),
                        clear: t('mergeVehicleModal.clearSelection'),
                    }}
                    fetchSuggestions={fetchSuggestions}
                    fetchTargets={fetchTargets}
                    renderRow={(item, { isSelected, label }) => (
                        <MergeCandidateRow
                            item={item}
                            label={label}
                            isSelected={isSelected}
                            fallbackIcon={
                                <CarIcon className="text-grey-300" size={28} />
                            }
                        />
                    )}
                />
            )}
        />
    );
}
